import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COLOR_PALETTE } from 'constants/ui';

import {
  retryStepExecutionAction,
  retryStepFromHereExecutionAction,
} from 'store/aiProcessRuns/actions';
import { selectIsRetryingFromHereStep, selectIsRetryingStep } from 'store/aiProcessRuns/selectors';

import { ROW_EXPANSION_SUMMARY_EVENT_COUNT } from '../AiActivity.constants';
import { AiActivityTestIds } from '../AiActivity.testIds';
import {
  formatClockTime,
  formatEventLabel,
  formatRetryFromHereScope,
  humanizeError,
  isTransientFailure,
} from '../AiActivity.utils';

import {
  containerStyles,
  copyRowStyles,
  eventItemStyles,
  eventsListStyles,
  eventTimeStyles,
  hintStyles,
  idValueStyles,
  recoveryRowStyles,
  sectionBodyStyles,
  sectionTitleStyles,
  technicalDetailsStyles,
  technicalLinkRowStyles,
  technicalLinkStyles,
} from './RunFailureForensics.styles';
import { RunFailureForensicsTestIds } from './RunFailureForensics.testIds';

const buildDiagnosticBundle = ({ runId, stepExecutionId, errorReason, errorMessage }) =>
  [
    runId ? `Run ID: ${runId}` : null,
    stepExecutionId ? `Step ID: ${stepExecutionId}` : null,
    errorReason ? `Error reason: ${errorReason}` : null,
    `Error: ${errorMessage ?? '(none captured)'}`,
  ]
    .filter(Boolean)
    .join('\n');

const RunFailureForensics = ({
  errorMessage,
  errorReason,
  recentEvents,
  recurringFailureCount,
  retryFromHereDependentCount,
  runId,
  stepExecutionId,
  canRetry,
  'data-testid': dataTestId,
}) => {
  const dispatch = useDispatch();
  const isRetrying = useSelector(selectIsRetryingStep(stepExecutionId));
  const isRetryingFromHere = useSelector(selectIsRetryingFromHereStep(stepExecutionId));
  const [showTechnical, setShowTechnical] = useState(false);
  const [isConfirmingRetry, setIsConfirmingRetry] = useState(false);
  const [isConfirmingRetryFromHere, setIsConfirmingRetryFromHere] = useState(false);
  const [copied, setCopied] = useState(false);

  const humanizedError = useMemo(
    () => humanizeError(errorMessage, errorReason),
    [errorMessage, errorReason],
  );
  const transient = useMemo(
    () => isTransientFailure(errorMessage, errorReason),
    [errorMessage, errorReason],
  );

  const onToggleTechnical = useCallback(() => {
    setShowTechnical(value => !value);
  }, []);

  const onCopyDiagnostics = useCallback(() => {
    const bundle = buildDiagnosticBundle({ runId, stepExecutionId, errorReason, errorMessage });

    navigator.clipboard?.writeText(bundle).then(() => setCopied(true));
  }, [runId, stepExecutionId, errorReason, errorMessage]);

  const onRetryClick = useCallback(() => setIsConfirmingRetry(true), []);
  const onRetryCancel = useCallback(() => setIsConfirmingRetry(false), []);
  const onRetryConfirm = useCallback(() => {
    setIsConfirmingRetry(false);
    dispatch(retryStepExecutionAction({ stepExecutionId }));
  }, [dispatch, stepExecutionId]);

  const onRetryFromHereClick = useCallback(() => setIsConfirmingRetryFromHere(true), []);
  const onRetryFromHereCancel = useCallback(() => setIsConfirmingRetryFromHere(false), []);
  const onRetryFromHereConfirm = useCallback(() => {
    setIsConfirmingRetryFromHere(false);
    dispatch(retryStepFromHereExecutionAction({ stepExecutionId }));
  }, [dispatch, stepExecutionId]);

  const summaryEvents = useMemo(() => {
    if (!_.isArray(recentEvents)) {
      return [];
    }

    return recentEvents.slice(-ROW_EXPANSION_SUMMARY_EVENT_COUNT);
  }, [recentEvents]);

  const showRetry = canRetry && !_.isNil(stepExecutionId);

  return (
    <Box data-testid={dataTestId} sx={containerStyles(transient)}>
      <Box>
        <Typography component="div" sx={sectionTitleStyles} variant="medium14">
          What happened
        </Typography>
        <Typography component="div" sx={sectionBodyStyles} variant="regular14">
          {humanizedError}
        </Typography>
      </Box>

      {transient && recurringFailureCount > 1 && (
        <Box sx={hintStyles}>
          <Typography variant="regular12">
            We saw {recurringFailureCount} similar hiccups on this process recently — usually a sign
            the issue is temporary.
          </Typography>
        </Box>
      )}

      {!_.isEmpty(summaryEvents) && (
        <Box>
          <Typography component="div" sx={sectionTitleStyles} variant="medium14">
            Activity before it stopped
          </Typography>
          <Box sx={eventsListStyles}>
            {summaryEvents.map((event, index) => (
              <Box key={event.id ?? index} sx={eventItemStyles}>
                <Typography sx={eventTimeStyles} variant="regular12">
                  {formatClockTime(event.timestamp)}
                </Typography>
                <Typography variant="regular12">{formatEventLabel(event)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {(showRetry || runId || stepExecutionId) && (
        <Box sx={recoveryRowStyles}>
          {showRetry &&
            (isConfirmingRetry ? (
              <>
                <Typography variant="regular12">
                  Re-running may repeat actions the step already took. Retry anyway?
                </Typography>
                <Button
                  data-testid={RunFailureForensicsTestIds.RETRY_CANCEL_BUTTON}
                  disabled={isRetrying}
                  size="small"
                  variant="line"
                  onClick={onRetryCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={AiActivityTestIds.RUN_FAILURE_RETRY_CONFIRM}
                  disabled={isRetrying}
                  size="small"
                  onClick={onRetryConfirm}
                >
                  Retry step
                </Button>
                {isRetrying ? <CircularProgress size={14} thickness={4} /> : null}
              </>
            ) : (
              <Button
                data-testid={AiActivityTestIds.RUN_FAILURE_RETRY}
                disabled={isRetrying}
                size="small"
                onClick={onRetryClick}
              >
                Retry step
              </Button>
            ))}
          {showRetry &&
            (isConfirmingRetryFromHere ? (
              <>
                <Typography variant="regular12">
                  {formatRetryFromHereScope(retryFromHereDependentCount)}
                </Typography>
                <Button
                  data-testid={RunFailureForensicsTestIds.RETRY_FROM_HERE_CANCEL_BUTTON}
                  disabled={isRetryingFromHere}
                  size="small"
                  variant="line"
                  onClick={onRetryFromHereCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={AiActivityTestIds.RUN_FAILURE_RETRY_FROM_HERE_CONFIRM}
                  disabled={isRetryingFromHere}
                  size="small"
                  onClick={onRetryFromHereConfirm}
                >
                  Retry from here
                </Button>
                {isRetryingFromHere ? <CircularProgress size={14} thickness={4} /> : null}
              </>
            ) : (
              <Button
                data-testid={AiActivityTestIds.RUN_FAILURE_RETRY_FROM_HERE}
                disabled={isRetryingFromHere}
                size="small"
                variant="line"
                onClick={onRetryFromHereClick}
              >
                Retry from here
              </Button>
            ))}
          {(runId || stepExecutionId) && (
            <Button
              data-testid={AiActivityTestIds.RUN_FAILURE_COPY_DIAGNOSTICS}
              size="small"
              variant="line"
              onClick={onCopyDiagnostics}
            >
              {copied ? 'Copied' : 'Copy diagnostics'}
            </Button>
          )}
        </Box>
      )}

      <Box sx={technicalLinkRowStyles}>
        <Box
          component="span"
          data-testid={AiActivityTestIds.TECHNICAL_DETAILS_TOGGLE}
          sx={technicalLinkStyles}
          onClick={onToggleTechnical}
        >
          <Typography component="span" variant="regular12">
            {showTechnical ? 'Hide' : 'Show'} technical details for engineering
          </Typography>
        </Box>
      </Box>

      {showTechnical && (
        <Box sx={technicalDetailsStyles}>
          {runId && (
            <Box sx={copyRowStyles}>
              <Typography component="span" variant="regular12">
                Run ID
              </Typography>
              <Typography component="span" sx={idValueStyles} variant="regular12">
                {runId}
              </Typography>
            </Box>
          )}
          {stepExecutionId && (
            <Box sx={copyRowStyles}>
              <Typography component="span" variant="regular12">
                Step ID
              </Typography>
              <Typography component="span" sx={idValueStyles} variant="regular12">
                {stepExecutionId}
              </Typography>
            </Box>
          )}
          {errorReason && (
            <Box sx={copyRowStyles}>
              <Typography component="span" variant="regular12">
                Reason
              </Typography>
              <Typography component="span" sx={idValueStyles} variant="regular12">
                {errorReason}
              </Typography>
            </Box>
          )}
          <Typography color={COLOR_PALETTE.GRAY[9]} component="div" variant="regular12">
            {errorMessage ?? 'No raw error message was stored — see the Raw → Error tab above.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

RunFailureForensics.propTypes = {
  errorMessage: PropTypes.string,
  errorReason: PropTypes.string,
  recentEvents: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.string,
      type: PropTypes.string,
      message: PropTypes.string,
    }),
  ),
  recurringFailureCount: PropTypes.number,
  retryFromHereDependentCount: PropTypes.number,
  runId: PropTypes.string,
  stepExecutionId: PropTypes.string,
  canRetry: PropTypes.bool,
  'data-testid': PropTypes.string,
};

export default memo(RunFailureForensics);
